/**
 * asyncThunk creators for Brand Kits in My Stuff, plus the helpers that dispatch them.
 *
 * @author Muhammad Shahrukh <shahrukh@250mils.com>
 */

import {createAsyncThunk} from '@reduxjs/toolkit';
import {getBrandFromList, getBrandsStateFromStore, isNameDifferentFromBrandName} from '@Libraries/brands-library';
import {executeThunk} from '@Utils/thunk.util';
import type {RootState} from '@/store';
import type {
  ActiveBrandId,
  Brand,
  BrandActivity,
  BrandAssetType,
  BrandColor,
  BrandColorType,
  BrandContentType,
  BrandFont,
  BrandListAssetType,
  BrandLogo,
  BrandTemplate,
  BrandVideo,
  BrandWritingStyle,
  FontType,
} from '@Components/mystuff-brands/brand-kits.types';
import {
  ADD_USER_IMAGE_TO_BRAND_URIS,
  BRAND_ASSET_SPECS,
  BRAND_CONTENT_TYPE_ACTIVITY_LOG,
  BrandsLoadingState,
  isBrandListAssetType,
} from '@Components/mystuff-brands/brand-kits.types';
import type {AddUserImageToBrandParams} from '@Components/mystuff-brands/brand-kits.types';
import {trackBrandKitRenamed} from '@Libraries/brand-kit-ga4.library';

const createBrandsThunk = createAsyncThunk.withTypes<{state: RootState}>();

/** Shape of UserBrands::getViewData() — every key is read by the extra reducers. */
export interface UserBrandsResponse {
  brands: Brand[];
  activeBrandId: ActiveBrandId;
  businessProfileBrandId: ActiveBrandId;
  isUserTeamAdmin: boolean;
  activeBrandLogos: BrandLogo[];
  activeBrandColors: BrandColor[];
  activeBrandFonts: BrandFont[];
  allLogosInUserBrands: BrandLogo[];
  allColorsInUserBrands: BrandColor[];
  allFontsInUserBrands: BrandFont[];
}

export interface NewBrandResponse {
  brand: Brand;
}

export interface NewBrandFromWebsiteResponse {
  brand: Brand;
  colors: BrandColor[];
}

export interface NewBrandFontResponse {
  brandId: string;
  idbrandFont: number;
}

export interface DeleteBrandResponse {
  /** The kit that became the team's default because the deleted kit was it; null when the default did not move. */
  newDefaultBrandId: string | null;
}

export interface NewBrandColorResponse {
  brandId: string;
  idbrandColor: number;
}

/** Each endpoint returns one asset shape; pairing it with its content type lets consumers narrow without casting. */
export type BrandContentPayload =
  | {contentType: BrandAssetType.BRAND_LOGOS; rows: BrandLogo[]}
  | {contentType: BrandAssetType.BRAND_IMAGES; rows: BrandLogo[]}
  | {contentType: BrandAssetType.BRAND_VIDEOS; rows: BrandVideo[]}
  | {contentType: BrandAssetType.BRAND_TEMPLATES; rows: BrandTemplate[]}
  | {contentType: BrandAssetType.BRAND_COLORS; rows: BrandColor[]}
  | {contentType: BrandAssetType.BRAND_FONTS; rows: BrandFont[]}
  | {contentType: typeof BRAND_CONTENT_TYPE_ACTIVITY_LOG; rows: BrandActivity[]};

export interface AddBrandParams {
  brandName: string;
  /** Present when the UI optimistically rendered a placeholder row before the server replied. */
  temporaryBrandId?: string | null;
  /** Whether the kit was user-created or AI-generated from a website. */
  source?: string | null;
  /** One of BRAND_CREATION_SOURCES.* — read by the extra reducers for GA4. */
  brandCreationSource?: string;
}

export interface AddBrandFontParams {
  brandId: string;
  idfont: number;
  fontFamily: string;
  brandFontName: string;
  defaultColor: string;
  fontType: FontType;
  fontSize: number;
  isBold: boolean;
  isItalic: boolean;
  isUnderlined: boolean;
  /** The font whose type this one takes over, demoted to OTHER by the extra reducers. */
  previouslySelectedBrandFontId?: number | null;
}

export interface AddBrandColorParams {
  brandId: string;
  hexCode: string;
  type: BrandColorType;
}

/** An update replaces the whole row; previouslySelectedBrandFontId names the font this one demotes to OTHER. */
export interface BrandFontUpdateParams extends BrandFont {
  previouslySelectedBrandFontId?: number | null;
}

export type UpdateBrandAssetParams =
  | {brandAssetType: BrandAssetType.BRAND_COLORS; brandAssetParams: BrandColor}
  | {brandAssetType: BrandAssetType.BRAND_FONTS; brandAssetParams: BrandFontUpdateParams};

export interface DeleteBrandAssetParams {
  /** Brand voice has no items to delete, so only the list-shaped types are addressable here. */
  brandAssetType: BrandListAssetType;
  idbrandAsset: number;
  brandId: string;
}

export const getBrandsAndActiveBrandsForUser = createAsyncThunk<UserBrandsResponse, undefined, {state: RootState}>(
  'getBrandsAndActiveBrandsForUser',
  async (_, {rejectWithValue}) => {
    try {
      return (await window.PMW.readLocal('brand/getBrandsAndActiveBrands')) as UserBrandsResponse;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
  {
    // Several components dispatch this on mount; the rejected reducer resets to NOT_LOADED so a failed fetch can retry.
    condition: (_, {getState}): boolean => {
      return getState().brands.brandsLoadingState === BrandsLoadingState.NOT_LOADED;
    },
  }
);

export const addNewBrand = createAsyncThunk<NewBrandResponse, AddBrandParams>('addNewBrand', async ({brandName}) => {
  return (await window.PMW.writeLocal('brand/newBrand', {name: brandName})) as NewBrandResponse;
});

export const addNewBrandFromWebsite = createAsyncThunk<NewBrandFromWebsiteResponse, AddBrandParams>('addNewBrandFromWebsite', async ({brandName}, {rejectWithValue}) => {
  try {
    return (await window.PMW.writeLocal('brand/newBrandFromWebsite', {name: brandName})) as NewBrandFromWebsiteResponse;
  } catch (err) {
    return rejectWithValue((err as Error).message);
  }
});

export interface UpdateBrandVoiceParams {
  brandId: string;
  /** null clears the setting. */
  aiWritingStyle: BrandWritingStyle | null;
  aiVoiceProfile: number | null;
}

export const updateBrandVoice = createAsyncThunk('updateBrandVoice', async ({brandId, aiWritingStyle, aiVoiceProfile}: UpdateBrandVoiceParams) => {
  await window.PMW.writeLocal('brand/updateBrandVoice', {brandId, aiWritingStyle: aiWritingStyle ?? '', aiVoiceProfile: aiVoiceProfile ?? ''});
});

/** Promoting a kit demotes the team's previous default, so the whole list is refetched after. */
export const setDefaultBrand = createBrandsThunk(
  'setDefaultBrand',
  async ({brandId}: {brandId: string}) => {
    await window.PMW.writeLocal('brand/setDefaultBrand', {brandId});
  },
  {
    // One save at a time: the store tracks a single kit being made default, so a second would race the first.
    condition: (_, {getState}): boolean => {
      return getState().brands.brandIdBeingMadeDefault === null;
    },
  }
);

export const changeBrandName = createAsyncThunk('changeBrandName', async ({brandId, newBrandName}: {brandId: string; newBrandName: string}) => {
  await window.PMW.writeLocal('brand/updateBrandName', {brandId, newName: newBrandName});
});

export const deleteBrand = createAsyncThunk<DeleteBrandResponse, {brandId: string; newActiveBrandId: string}>('deleteBrand', async ({brandId, newActiveBrandId}) => {
  return (await window.PMW.writeLocal('brand/deleteBrand', {brandId, newActiveBrandId})) as DeleteBrandResponse;
});

export const addBrandFont = createAsyncThunk<NewBrandFontResponse, AddBrandFontParams>('addNewBrandFont', async (fontInfo) => {
  return (await window.PMW.writeLocal('brand/createNewBrandFont', {
    brandId: fontInfo.brandId,
    idfont: fontInfo.idfont,
    fontFamily: fontInfo.fontFamily,
    brandFontName: fontInfo.brandFontName,
    defaultColor: fontInfo.defaultColor,
    fontType: fontInfo.fontType,
    fontSize: fontInfo.fontSize,
    isBold: fontInfo.isBold,
    isItalic: fontInfo.isItalic,
    isUnderlined: fontInfo.isUnderlined,
    previouslySelectedBrandFontId: fontInfo.previouslySelectedBrandFontId,
  })) as NewBrandFontResponse;
});

export const addBrandColor = createAsyncThunk<NewBrandColorResponse, AddBrandColorParams>('addBrandColor', async (colorInfo) => {
  return (await window.PMW.writeLocal('brand/createNewBrandColor', colorInfo)) as NewBrandColorResponse;
});

export const updateBrandAsset = createAsyncThunk('updateBrandAsset', async ({brandAssetType, brandAssetParams}: UpdateBrandAssetParams) => {
  await window.PMW.writeLocal(getAjaxRequestUriForUpdatingBrandAsset(brandAssetType), {...brandAssetParams});
});

export const getContentForBrandContentType = createAsyncThunk<BrandContentPayload, {brandId: string; brandContentType: BrandContentType}>(
  'getContentForBrandContentType',
  async ({brandId, brandContentType}) => {
    const rows: unknown = await window.PMW.readLocal(getAjaxRequestUriForFetchingBrandContentType(brandContentType), {brandId});
    // Anything but a list marks the slot failed rather than handing the reducer rows it would crash on.
    if (!Array.isArray(rows)) {
      throw new Error(`Brand content ${brandContentType} for kit ${brandId} did not come back as a list`);
    }

    return {contentType: brandContentType, rows} as BrandContentPayload;
  }
);

export const deleteBrandAsset = createAsyncThunk('deleteBrandAsset', async ({brandAssetType, idbrandAsset, brandId}: DeleteBrandAssetParams) => {
  await window.PMW.writeLocal(BRAND_ASSET_SPECS[brandAssetType].deleteUri, {idbrandAsset, brandId});
});

export const changeActiveBrandId = createAsyncThunk('changeActiveBrandId', async ({newActiveBrandId}: {newActiveBrandId: string}) => {
  await window.PMW.writeLocal('brand/updateActiveBrandId', {brandId: newActiveBrandId});
});

export const changePrimaryLogo = createAsyncThunk('changePrimaryLogo', async ({idbrandLogo, brandId}: {idbrandLogo: number; brandId: string}) => {
  await window.PMW.writeLocal('brand/changePrimaryLogo', {idbrandLogo, brandId});
});

export const getAllBrandsWithAssets = createAsyncThunk<UserBrandsResponse>('getAllBrandsWithAssets', async () => {
  return (await window.PMW.readLocal('brand/getAllBrandsWithAssets')) as UserBrandsResponse;
});

/**
 * The same every-kit read, for the kit pills and "+N more" list, which need each kit's first logo, colours and heading font.
 * It leaves the page's loading state alone, so the Brand Kits page never drops back to its skeleton for it.
 */
export const getBrandKitPreviewAssets = createAsyncThunk<UserBrandsResponse>('getBrandKitPreviewAssets', async () => {
  return (await window.PMW.readLocal('brand/getAllBrandsWithAssets')) as UserBrandsResponse;
});

/** Fetches one asset slot of a kit into the store, reporting a failure through the default error modal. */
export const fetchBrandAssets = async (brandId: string, brandAssetType: BrandListAssetType): Promise<void> => {
  return executeThunk(() => {
    return window.PMW.redux.store.dispatch(getContentForBrandContentType({brandId, brandContentType: brandAssetType}));
  });
};

export interface AddBrandVideoResponse {
  brandVideo: BrandVideo;
}

export interface AddBrandTemplateResponse {
  brandTemplate: BrandTemplate;
}

/** Links one of the user's existing uploads into a kit as a logo or brand image. */
export const addUserImageToBrand = createAsyncThunk('addUserImageToBrand', async ({brandId, hashedFilename, brandAssetType}: AddUserImageToBrandParams): Promise<void> => {
  await window.PMW.writeLocal(ADD_USER_IMAGE_TO_BRAND_URIS[brandAssetType], {brandId, hashedFilename});
});

export const addBrandVideo = createAsyncThunk<AddBrandVideoResponse, {brandId: string; hashedFilename: string; name?: string}>('addBrandVideo', async (videoInfo) => {
  return (await window.PMW.writeLocal('brand/addBrandVideo', videoInfo)) as AddBrandVideoResponse;
});

/** Links one of the user's designs into the kit as a reference, never a copy. */
export const addBrandTemplate = createAsyncThunk<AddBrandTemplateResponse, {brandId: string; posterHashedId: string}>('addBrandTemplate', async (templateInfo) => {
  return (await window.PMW.writeLocal('brand/addBrandTemplate', templateInfo)) as AddBrandTemplateResponse;
});

/**
 * @param ajaxOpts data to send to the server, plus any params the extra reducers read
 * @param brandCreationSource where in the UI the flow started — one of BRAND_CREATION_SOURCES.*
 */
export const createBrandWithActiveContext = async (ajaxOpts: AddBrandParams, brandCreationSource: string): Promise<void> => {
  return executeThunk(() => {
    return window.PMW.redux.store.dispatch(addNewBrand({...ajaxOpts, brandCreationSource}));
  });
};

export const createBrandFromWebsite = async (
  ajaxOpts: AddBrandParams,
  onErrorCallback: (error: unknown, message?: string, logError?: boolean) => void,
  onSuccessCallback: () => void
): Promise<void> => {
  return executeThunk(
    () => {
      return window.PMW.redux.store.dispatch(addNewBrandFromWebsite({...ajaxOpts}));
    },
    onSuccessCallback,
    (response: {payload?: unknown; error?: unknown}) => {
      if (response.payload === 'unable_to_extract') {
        onErrorCallback(response.payload, window.i18next.t('pmwjs_brand_kit_create_failed'), false);
      } else {
        onErrorCallback(response.error, window.i18next.t('pmwjs_brand_kit_create_error'), false);
      }
    }
  );
};

export const editBrand = async (brandId: string, newBrandName: string): Promise<void> => {
  const {brands} = getBrandsStateFromStore();
  const brandToEdit = getBrandFromList(brandId, brands);

  if (!isNameDifferentFromBrandName(brandToEdit, newBrandName)) {
    return;
  }

  return executeThunk(() => {
    return window.PMW.redux.store.dispatch(changeBrandName({brandId, newBrandName}));
  }, trackBrandKitRenamed);
};

const getAjaxRequestUriForUpdatingBrandAsset = (brandAssetType: BrandListAssetType): string => {
  const updateUri = BRAND_ASSET_SPECS[brandAssetType].updateUri;

  if (!updateUri) {
    throw new Error(`Brand asset type ${String(brandAssetType)} has no update endpoint`);
  }

  return updateUri;
};

const getAjaxRequestUriForFetchingBrandContentType = (brandContentType: BrandContentType): string => {
  if (brandContentType === BRAND_CONTENT_TYPE_ACTIVITY_LOG) {
    return 'brand/getBrandActivity';
  }

  if (!isBrandListAssetType(brandContentType)) {
    throw new Error(`Brand content type ${String(brandContentType)} has no fetch endpoint`);
  }

  return BRAND_ASSET_SPECS[brandContentType].fetchUri;
};
